import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { SectionHeader, searchBoxSx } from "../../components/detailLayout";
import TabsCst from "@components/TabsCst";
import FiltersAccordionCst from "@components/FiltersAccordionCst";
import { FiltersBarValue } from "@components/FiltersBarCst/types";
import { PageFilterField } from "@hooks/usePageFilters";
import { PagesID } from "@utils/utilsConfigurations";
import {
  Folder,
  Folders,
  GalleryVerticalEnd,
  GraduationCap,
  ListFilter,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { ServicesURL, downloadBlobFile } from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import PaginatorCst from "@components/PaginatorCst";
// import SortMenuCst from "@components/SortMenuCst";
import { SearchInput } from "src/UI/SearchInput";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { mapAggregatedType } from "../../../Catalog/types";
import UserLoEnrollmentRow from "./UserLoEnrollmentRow";
import EnrollmentHistoryModal from "../../../Catalog/UsersAndGroups/components/EnrollmentHistoryModal";
import EditEnrollmentModal, {
  buildConfigFromEnrollment,
  disabledByPriority,
} from "../../../Catalog/UsersAndGroups/components/EditEnrollmentModal";
import EnrollLOModal from "@pages/Contents/V2/Corporate/UsersGroups/components/EnrollLOModal";
import { EnrollType } from "../../../Catalog/Enrollment/types";
// import SolicitConfirmDialog, {
// SolicitTarget,
// } from "../../../Catalog/UsersAndGroups/components/SolicitConfirmDialog";
import { PERMISSIONS, usePermissions } from "@utils/permissions";
import EnrollmentProgressModal from "../../../Catalog/EnrollmentProgress/EnrollmentProgressModal";
import { EnrollmentProgressTrigger } from "../../../Catalog/EnrollmentProgress/types";

const PAGE_SIZE = 5;
const LO_TYPE_BY_TAB: Record<string, string> = {
  activities: "ACTIVITY",
  courses: "COURSE",
  paths: "PATH",
};
const ACTIVITY_CATEGORIES = [
  "DA_XAPI",
  "DA_SCORM",
  "DA_PHYSICAL_CLASS",
  "DA_VIRTUAL_CLASS",
  "DA_EXTERNAL_RES",
  "DA_YOUTUBE",
  "DA_LINKEDIN",
  "DA_SURVEY",
  "DA_URL_RES",
  "DA_MATERIAL",
  "DA_GOALS",
  "DA_MEETING",
  "DA_FINAL_BALANCE",
  "DA_LTI",
];
const COURSE_PATH_CATEGORIES = ["BLENDED", "ASYNC", "SYNC"];

const STATUS_ENROLLMENT_OPTIONS_NO_NOT_ENROLLED = [
  "E",
  "W",
  "C",
  "P",
  "R",
  "T",
  "PA",
  "PW",
  "PA0",
  "X",
];

interface LearningObjectSectionProps {
  userName?: string;
}

export const LearningObjectSection = ({
  userName,
}: LearningObjectSectionProps) => {
  const { t: tUserDetail } = useTranslation("user-detail-v2");
  const { t } = useTranslation("catalog-v2");
  const { t: tRoles } = useTranslation("input");
  const { t: tEnroll } = useTranslation("users-and-groups");

  const theme = useTheme();

  const { id: corporateId = "", userId = "" } = useParams();

  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<{
    entity: any;
    scope: "single" | "group";
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<any>(null);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [editGroupTarget, setEditGroupTarget] = useState<any>(null);
  const [groupEnrollSource, setGroupEnrollSource] = useState<any>(null);
  // const [solicitTarget, setSolicitTarget] = useState<SolicitTarget | null>(
  // null,
  // );
  const { can } = usePermissions();
  // const canSolicit = can(PERMISSIONS.enrollment.reminder);
  // Come v1 (pagina iscritti al LO): modifica / elimina iscrizione e storico
  // con update.enrollment.write, scarica certificato con certificate.issued.read.
  const canUpdateEnrollment = can(PERMISSIONS.enrollment.update);
  const canDownloadCertificate = can(PERMISSIONS.certificate.issuedRead);
  const canMonitorProgress = can(PERMISSIONS.enrollment.statusMonitor);
  const [progressTrigger, setProgressTrigger] =
    useState<EnrollmentProgressTrigger | null>(null);

  const [loTab, setLoTab] = useState("all");
  const [loPage, setLoPage] = useState(1);
  const [loPageSize, setLoPageSize] = useState(PAGE_SIZE);
  const [loSearch, setLoSearch] = useState("");
  // TODO Ordinamento: nascosto perche' l'endpoint student-learning-objects
  // ignora il parametro sort, da riattivare quando il BE lo gestira'.
  // const [loSort, setLoSort] = useState("most-recent");
  const [fieldFilters, setFieldFilters] = useState<FiltersBarValue>({});

  const statusFilterOptions = useMemo(
    () =>
      STATUS_ENROLLMENT_OPTIONS_NO_NOT_ENROLLED.map((value) => ({
        value,
        label: String(
          tRoles(`options.status_enrollment.${value.toLowerCase()}`),
        ),
      })),
    [tRoles],
  );

  const filterExcludeKeys = useMemo(
    () => (loTab === "all" ? ["title"] : ["title", "type"]),
    [loTab],
  );

  const disabledFilterOptions = (
    field: PageFilterField,
    value: FiltersBarValue,
  ): string[] | undefined => {
    if (field.key === "category") {
      const effectiveType =
        loTab !== "all" ? LO_TYPE_BY_TAB[loTab] : (value.type as string);
      if (effectiveType === "COURSE" || effectiveType === "PATH") {
        return ACTIVITY_CATEGORIES;
      }
      if (effectiveType === "ACTIVITY") return COURSE_PATH_CATEGORIES;
      return undefined;
    }
    if (field.key === "type") {
      const category = value.category as string;
      if (COURSE_PATH_CATEGORIES.includes(category)) return ["ACTIVITY"];
      if (ACTIVITY_CATEGORIES.includes(category)) return ["COURSE", "PATH"];
    }
    return undefined;
  };

  const loUrl = userId
    ? ServicesURL.getLOBasedOnStudentId
        .replace(":idCorporate", corporateId || "1")
        .replace(":studentId", userId)
    : null;

  const loTypeQueryParams = useMemo(
    () =>
      loTab !== "all" && LO_TYPE_BY_TAB[loTab]
        ? { type: LO_TYPE_BY_TAB[loTab] }
        : undefined,
    [loTab],
  );

  const {
    items: paginatedLO,
    totalItems: loTotal,
    totalPages: loPages,
    isLoading: loadingLo,
    mutate,
  } = usePaginatedList(corporateId && userId ? loUrl : null, {
    page: loPage,
    pageSize: loPageSize,
    search: loSearch || undefined,
    fieldFilters: { ...fieldFilters, ...loTypeQueryParams },
  });

  const hasActiveFilters = useMemo(() => {
    const hasValue = (val: any): boolean => {
      if (val === undefined || val === null || val === "") return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === "object") return Object.values(val).some(hasValue);
      return true;
    };
    return Object.values(fieldFilters || {}).some(hasValue);
  }, [fieldFilters]);

  const displayLoading = loadingLo;
  const displayTotalItems = loTotal;
  const displayTotalPages = loPages;
  const displayEmpty = paginatedLO.length === 0;

  const loTabs = [
    {
      key: "all",
      label: t("tabs.all"),
      icon: <GalleryVerticalEnd size={"0.75rem"} />,
    },
    {
      key: "activities",
      label: t("tabs.activities"),
      icon: <GraduationCap size={"0.75rem"} />,
    },
    {
      key: "courses",
      label: t("tabs.courses"),
      icon: <Folder size={"0.75rem"} />,
    },
    {
      key: "paths",
      label: t("tabs.paths"),
      icon: <Folders size={"0.75rem"} />,
    },
  ];

  // const sortOptions = [
  //   { label: t("sort.most-recent"), value: "most-recent" },
  //   { label: t("sort.least-recent"), value: "least-recent" },
  //   { label: t("sort.alphabetical"), value: "alphabetical" },
  // ];

  useEffect(() => {
    if (!displayLoading && loPage > displayTotalPages) {
      setLoPage(Math.max(displayTotalPages, 1));
    }
  }, [displayLoading, loPage, displayTotalPages]);

  const confirmDelete = async () => {
    if (!deleteTarget || !canUpdateEnrollment) return;
    const { entity: target, scope } = deleteTarget;
    if (scope === "single" && !target.enrollId) return;
    if (
      scope === "group" &&
      (target.massiveGroupId == null || !target.groupType)
    )
      return;
    setIsDeleting(true);
    try {
      const url =
        scope === "group"
          ? `${ServicesURL.postEnrollmentMassive}?corporateId=${corporateId}&groupId=${target.massiveGroupId}&groupType=${target.groupType}&objectId=${target.id}`
          : `${ServicesURL.deleteEnrollment}?enrollmentEId=${target.enrollId}`;
      await fetcher(url, MethodType.DELETE);
      setDeleteTarget(null);
      mutate();
      openToast({
        severity: "success",
        message: String(tEnroll("card.delete-confirm.success")),
      });
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(tEnroll("card.delete-confirm.error")),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const downloadCertificate = async (target: any) => {
    const certificateIds: number[] = target.certificateIds ?? [];
    if (!canDownloadCertificate || certificateIds.length === 0 || !userId)
      return;
    try {
      if (certificateIds.length === 1) {
        const url = `${ServicesURL.getIssuedCertificate.replace(
          ":certificateId",
          String(certificateIds[0]),
        )}?objectId=${target.id}&learnerId=${userId}`;
        await downloadBlobFile(
          url,
          null,
          `issued_certificate_lo_${target.id}_learner_${userId}.pdf`,
          MethodType.GET,
        );
      } else {
        // Piu' certificati sullo stesso LO: download unico come zip
        await downloadBlobFile(
          ServicesURL.getIssuedCertificatesZip,
          {
            issuedCertificates: certificateIds.map((certificateId) => ({
              learnerId: userId,
              objectId: target.id,
              issuedCertificateId: certificateId,
            })),
          },
          `issued_certificates_lo_${target.id}_learner_${userId}.zip`,
        );
      }
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(tEnroll("card.certificate-error")),
      });
    }
  };

  // const openSolicit = (target: any) => {
  // if (!canSolicit) return;
  // setSolicitTarget({
  // scope: "user",
  // name: userName || `ID ${userId}`,
  // corporateId,
  // objectId: target.id,
  // objectType: target.entityType,
  // userId,
  // });
  // };

  const openProgress = (target: any) => {
    if (!canMonitorProgress) return;
    setProgressTrigger({
      corporateId,
      userId,
      userName,
      objectId: target.id,
      objectType: target.entityType,
      objectTitle: target.title,
      category: target.type,
      status: target.status,
      completeDate: target.completeDate,
      progPerc: target.progPerc,
      learningObjectId: target.id_learning_object ?? null,
      activityId: target.activity_id ?? null,
      forceable: target.forceable,
      onSuccess: mutate,
      source: "user-detail",
    });
  };

  const deleteTargetName = deleteTarget?.entity?.title ?? "";

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader
          sx={{ mb: 1 }}
          title={tUserDetail("lo.title")}
          subtitle={tUserDetail("lo.subtitle")}
        />

        <TabsCst
          tabs={loTabs}
          activeKey={loTab}
          onChange={(k) => {
            setLoTab(k);
            setLoPage(1);
            if (k !== "all") {
              setFieldFilters(({ type, ...rest }) => rest);
            }
          }}
        />
        <Stack
          flexDirection={"row"}
          gap={"1rem"}
          flexWrap={"wrap"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Box sx={searchBoxSx}>
            <SearchInput
              id="user-detail-lo-search"
              value={loSearch ?? ""}
              placeholder={tUserDetail("search")}
              onChange={(e: any) => {
                setLoSearch(e.target.value);
                setLoPage(1);
              }}
              sx={{ width: "100%" }}
            />
          </Box>
          <Stack
            flexDirection={"row"}
            gap={"1rem"}
            flexWrap={"wrap"}
            alignItems={"center"}
          >
            <ButtonSecondary
              size="md"
              startIcon={<ListFilter size={16} />}
              onClick={() => setFiltersPanelOpen((prev) => !prev)}
              sxButton={
                hasActiveFilters || filtersPanelOpen
                  ? { borderColor: theme.palette.systemSecondary01 }
                  : undefined
              }
            >
              {t("filters-button")}
            </ButtonSecondary>
            {/* <SortMenuCst
              id="user-detail-lo-sort"
              value={loSort}
              options={sortOptions}
              onChange={(next) => {
                setLoSort(next || "most-recent");
                setLoPage(1);
              }}
            /> */}
          </Stack>
        </Stack>

        <FiltersAccordionCst
          open={filtersPanelOpen}
          onClose={() => setFiltersPanelOpen(false)}
          pageId={PagesID["corporate.users.los.overall"]}
          value={fieldFilters}
          onApply={(next) => {
            setFieldFilters(next);
            setLoPage(1);
          }}
          excludeKeys={filterExcludeKeys}
          dynamicOptions={{ status: statusFilterOptions }}
          optionsToDisable={disabledFilterOptions}
        />

        {displayLoading ? (
          <CircularProgress />
        ) : displayEmpty ? (
          <Typography sx={{ color: theme.palette.text.tertiary }}>
            {tUserDetail("lo.empty")}
          </Typography>
        ) : (
          paginatedLO.map((raw: any, index: number) => {
            const loId = raw.id ?? raw.activity_id ?? index;
            const objectId = raw.id ?? raw.activity_id;
            const entityType = mapAggregatedType(raw.type);
            const entity = {
              ...raw,
              id: loId,
              title: raw.title || raw.name || raw.activity_name,
              type: raw.category ?? raw.type,
              entityType,
              userId,
              enrollId: raw.enrollId ?? raw.enroll_id,
              enrollType: raw.enrollType ?? raw.enroll_type,
              mandatory: raw.mandatory ?? raw.is_mandatory,
              massiveGroupId: raw.massiveGroupId ?? raw.massive_group_id,
              groupType: raw.groupType ?? raw.group_type,
              expirationDate: raw.expirationDate ?? raw.expiration_date,
              daysToComplete: raw.daysToComplete ?? raw.days_to_complete,
              isExpirationFixed:
                raw.isExpirationFixed ?? raw.is_expiration_fixed,
              isExpirationHard: raw.isExpirationHard ?? raw.is_expiration_hard,
              attributes: raw.attributes ?? raw.attributes_ids ?? [],
              enrollDate: raw.enrollDate ?? raw.enroll_date,
              completeDate: (raw.completeDate ?? raw.complete_date) || null,
              progPerc: raw.progPerc ?? raw.progr_perc ?? raw.prog_perc,
              certificateIds: (
                raw.certificateIds ??
                raw.certificate_ids ??
                (raw.certificate_id != null ? [raw.certificate_id] : [])
              ).filter((certId: unknown) => certId != null),
            };
            return (
              <UserLoEnrollmentRow
                key={loId}
                entity={entity}
                entityType={entityType}
                onEditEnrollment={
                  objectId ? () => setEditTarget(entity) : undefined
                }
                onDeleteEnrollment={
                  objectId
                    ? () => setDeleteTarget({ entity, scope: "single" })
                    : undefined
                }
                onEditGroupEnrollment={
                  objectId ? () => setEditGroupTarget(entity) : undefined
                }
                onDeleteGroupEnrollment={
                  objectId
                    ? () => setDeleteTarget({ entity, scope: "group" })
                    : undefined
                }
                onHistory={
                  objectId ? () => setHistoryTarget(entity) : undefined
                }
                onDownloadCertificate={
                  objectId ? () => downloadCertificate(entity) : undefined
                }
                // onSolicit={objectId ? () => openSolicit(entity) : undefined}
                onViewProgress={
                  objectId ? () => openProgress(entity) : undefined
                }
                // S05: azioni previste ma senza permesso mostrate disabilitate
                // (permessi: P25-LO, P70, P73, D05).
                disabledActions={{
                  edit: !canUpdateEnrollment,
                  delete: !canUpdateEnrollment,
                  editGroup: !canUpdateEnrollment,
                  deleteGroup: !canUpdateEnrollment,
                  history: !canUpdateEnrollment,
                  certificate: !canDownloadCertificate,
                  // solicit: !canSolicit,
                  progress: !canMonitorProgress,
                }}
              />
            );
          })
        )}

        {displayTotalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <PaginatorCst
              page={loPage}
              pageSize={loPageSize}
              totalItems={displayTotalItems}
              onPageChange={(next) => setLoPage(next)}
              onPageSizeChange={(next) => {
                setLoPageSize(next);
                setLoPage(1);
              }}
            />
          </Box>
        )}
      </Box>

      <ConfirmDialogCst
        open={!!deleteTarget}
        title={String(
          tEnroll(
            deleteTarget?.scope === "group"
              ? "card.delete-group-confirm.title"
              : "card.delete-confirm.title",
          ),
        )}
        description={String(
          tEnroll(
            deleteTarget?.scope === "group"
              ? "card.delete-group-confirm.description"
              : "card.delete-confirm.description",
            { name: deleteTargetName },
          ),
        )}
        confirmLabel={String(tEnroll("card.delete-confirm.confirm"))}
        cancelLabel={String(tEnroll("card.delete-confirm.cancel"))}
        confirmVariant="danger"
        isSubmitting={isDeleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      {/* <SolicitConfirmDialog
        target={solicitTarget}
        onClose={() => setSolicitTarget(null)}
      /> */}

      <EnrollmentProgressModal
        trigger={progressTrigger}
        onClose={() => setProgressTrigger(null)}
      />

      <EnrollmentHistoryModal
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        objectId={historyTarget?.id ?? 0}
        objectTitle={historyTarget?.title}
        user={historyTarget}
      />

      <EditEnrollmentModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        corporateId={corporateId}
        objectId={editTarget?.id ?? 0}
        objectType={editTarget?.entityType}
        objectTitle={editTarget?.title}
        user={editTarget}
        onSuccess={mutate}
      />

      <ConfirmDialogCst
        open={!!editGroupTarget}
        title={String(tEnroll("card.edit-group-confirm.title"))}
        description={String(
          tEnroll("card.edit-group-confirm.description", {
            groupId: editGroupTarget?.massiveGroupId ?? "-",
          }),
        )}
        confirmLabel={String(tEnroll("card.edit-group-confirm.confirm"))}
        cancelLabel={String(tEnroll("card.delete-confirm.cancel"))}
        onClose={() => setEditGroupTarget(null)}
        onConfirm={() => {
          setGroupEnrollSource(editGroupTarget);
          setEditGroupTarget(null);
        }}
      />

      <EnrollLOModal
        open={!!groupEnrollSource}
        onClose={() => setGroupEnrollSource(null)}
        subjectType="group"
        subject={
          groupEnrollSource
            ? {
                id: groupEnrollSource.massiveGroupId,
                name:
                  groupEnrollSource.groupName ||
                  `ID ${groupEnrollSource.massiveGroupId}`,
              }
            : null
        }
        groupType={groupEnrollSource?.groupType ?? "STATIC"}
        corporateId={String(corporateId)}
        onSuccess={mutate}
        initialStep="config"
        initialSelection={
          groupEnrollSource
            ? [
                {
                  id: groupEnrollSource.id,
                  kind: groupEnrollSource.entityType,
                  title: groupEnrollSource.title ?? "",
                  entity: {},
                },
              ]
            : []
        }
        initialConfig={
          groupEnrollSource
            ? buildConfigFromEnrollment(groupEnrollSource)
            : undefined
        }
        extraDisabledEnrollTypes={disabledByPriority(
          groupEnrollSource?.enrollType as EnrollType | undefined,
        )}
      />
    </>
  );
};
